import React from 'react';
import { language } from '../LanguageService';
import { ToastNotification } from '../../components/Carbon';
import { getRandomId, iterableToArray } from '../../helpers/helpers';
import { Subject } from 'rxjs';
/**
 * Create a Carbon Notification to show on the DOM
 *
 * The root component should use this service to get the list of Notifications to render
 * and subscribe to the update pusher
 */
export class NotificationService {
    constructor() {
        /** Map of notifications currently available to render on UI */
        this.notifications = new Map();
        /** Data propagation subscription */
        this.notifier = new Subject();
        /**
         * Remove a notification from the current notifications
         *
         * @param id - the ID of the notification that was assigned on creation
         */
        this.remove = (id) => {
            this.notifications.delete(id);
        };
    }
    /**
     * Create a notification to show on the DOM
     *
     * @param kind - The type of notification to show
     * @param title - The title to show on the notification
     * @param subtitle - The subtitle to show, this can be either a string or a custom ReactNode
     * @param timeout - How long to show the notification for in milliseconds. Set to 0 to make it stay until user dismisses
     *
     * @returns an ID of the notification
     */
    create(kind, title, subtitle, timeout = 7000) {
        const caption = language.getDate(new Date().toISOString(), 'mediumTime');
        const id = getRandomId();
        const notification = (React.createElement(ToastNotification, { key: id, "data-auto": `notification-${kind}-${title}`, kind: kind, title: title, subtitle: subtitle, timeout: timeout, 
            // eslint-disable-next-line
            role: "alert", "aria-live": "polite", "aria-modal": "true", "aria-label": language.get('modal.close'), caption: caption, "aria-description": title + ' - ' + subtitle || '', onClose: () => {
                this.remove(id);
            }, onCloseButtonClick: () => {
                this.remove(id);
            } }));
        this.notifications.set(id, notification);
        this.notifier.next(true);
        return id;
    }
    /**
     * Allow components to subscribe to watch for notification updates and know to update state
     *
     * @remarks should only be used by the app root
     *
     * @returns an observable that can be subscribed to
     */
    get notificationNotifier() {
        return this.notifier.asObservable();
    }
    /**
     * Returns an array of Notifications (ReactNode) to render on the UI
     *
     * @returns an array of ReactNodes to render
     */
    get notificationsToRender() {
        return iterableToArray(this.notifications.values());
    }
}
/** The shared notification service */
export const notification = new NotificationService();
